import chalk from 'chalk';
import ora from 'ora';
import { confirm } from '@inquirer/prompts';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import { unwrapChannelConfig, type ChannelConfigRowEnvelope } from '../lib/channel-config.js';

/**
 * `agt channels beam setup <agent-code-name>`
 */
export async function channelBeamSetupCommand(
  agentCodeName: string,
  options: {
    directoryUrl?: string;
    skipRegister?: boolean;
    autoPublish?: boolean;
    force?: boolean;
  },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Looking up agent "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    // Look up agent via API
    const agentData = await api.get<{
      agent: {
        agent_id: string;
        code_name: string;
        display_name: string;
        team_id: string;
        channels: string[];
      };
    }>(`/agents/${encodeURIComponent(agentCodeName)}`);

    const agent = agentData.agent;

    // Verify beam is in the agent's channel list
    const channels: string[] = agent.channels ?? [];
    if (!channels.includes('beam')) {
      spinner.fail(`Agent "${agentCodeName}" does not have Beam in its channel list.`);
      error('Enable Beam in the agent\'s channels first (add "beam" to the channels array).');
      process.exitCode = 1;
      return;
    }

    spinner.succeed(`Found agent "${agentCodeName}"`);

    // ENG-8540: this guard existed before but read `existingConfig.config.beam_id`,
    // one level short of the payload, so it was permanently false and the
    // confirmation never appeared. A silent keypair overwrite was the result.
    //
    // The `.catch` deliberately no longer swallows every error into "no existing
    // identity": an auth failure or a 500 must not read as "safe to overwrite".
    // Only a 404 (no config row yet) is a legitimate absence.
    let existing: ChannelConfigRowEnvelope<Record<string, unknown>> | null = null;
    try {
      existing = await api.get<ChannelConfigRowEnvelope<Record<string, unknown>>>(
        `/agents/${encodeURIComponent(agentCodeName)}/channel-configs/beam`,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (!/\b404\b|not found/i.test(message)) {
        spinner.fail('Could not check for an existing Beam identity');
        error(`${message}\n\nRefusing to continue: overwriting an identity you cannot see is not safe.`);
        process.exitCode = 1;
        return;
      }
    }

    const existingBeam = unwrapChannelConfig(existing).payload;
    let regenerating = false;
    if (existingBeam?.beam_id) {
      regenerating = true;
      info(`Agent already has Beam identity: ${chalk.cyan(String(existingBeam.beam_id))}`);
      if (json) {
        // Never prompt in --json mode; require the explicit flag instead, so a
        // scripted caller cannot destroy an identity by omission.
        if (!options.force) {
          spinner.stop();
          error('Agent already has a Beam identity. Re-run with --force to regenerate it.');
          process.exitCode = 1;
          return;
        }
      } else {
        const proceed = await confirm({
          message: 'Regenerate identity? This creates a NEW keypair; the current one stops working.',
          default: false,
        });
        if (!proceed) return;
      }
    }

    // Generate identity and register
    spinner.start('Generating Ed25519 identity and registering with Beam directory…');

    // ENG-8540: the previous target was an `/agents/.../channel-configs/beam`
    // sub-path that has never been registered on the API - every invocation
    // 404'd, so `beam setup` could not have worked at all. The real route is
    // `POST /channels/beam/create`, and it keys on agent_id, not code_name.
    const result = await api.post<{
      ok: boolean;
      beam_id: string;
      did: string;
      public_key: string;
      trust_score: number;
      verification_tier: string;
      published_capabilities: string[];
      error?: string;
    }>('/channels/beam/create', {
      agent_id: agent.agent_id,
      auto_publish_capabilities: options.autoPublish ?? true,
      directory_url: options.directoryUrl,
      force: regenerating,
    });

    if (!result.ok) {
      spinner.fail('Registration failed');
      error(result.error ?? 'Unknown error');
      process.exitCode = 1;
      return;
    }

    spinner.succeed('Beam identity created and registered');

    if (json) {
      jsonOutput({
        beam_id: result.beam_id,
        did: result.did,
        public_key: result.public_key,
        trust_score: result.trust_score,
        verification_tier: result.verification_tier,
        published_capabilities: result.published_capabilities,
      });
      return;
    }

    console.log();
    table(
      ['Field', 'Value'],
      [
        ['Beam ID', chalk.cyan(result.beam_id)],
        ['DID', chalk.dim(result.did)],
        ['Trust Score', String(result.trust_score)],
        ['Verification', result.verification_tier],
        ['Capabilities', result.published_capabilities.length > 0
          ? result.published_capabilities.join(', ')
          : chalk.dim('none published')],
      ],
    );
    console.log();
    success(`Agent "${agentCodeName}" is now reachable at ${chalk.cyan(result.beam_id)}`);
  } catch (err) {
    spinner.fail('Setup failed');
    error(err instanceof Error ? err.message : 'Unknown error');
    process.exitCode = 1;
  }
}

/**
 * `agt channels beam status <agent-code-name>`
 */
export async function channelBeamStatusCommand(agentCodeName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Fetching Beam config for "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    const data = await api.get<ChannelConfigRowEnvelope<Record<string, unknown>>>(
      `/agents/${encodeURIComponent(agentCodeName)}/channel-configs/beam`,
    );

    const { payload: cfg, status: rowStatus } = unwrapChannelConfig(data);

    if (!cfg?.beam_id) {
      spinner.info('No Beam identity configured for this agent.');
      return;
    }

    spinner.succeed('Beam configuration found');

    if (json) {
      jsonOutput(cfg);
      return;
    }

    console.log();
    table(
      ['Field', 'Value'],
      [
        ['Beam ID', chalk.cyan(String(cfg.beam_id))],
        ['DID', chalk.dim(String(cfg.did ?? '—'))],
        ['Public Key', chalk.dim(String(cfg.public_key ?? '').slice(0, 24) + '…')],
        ['Trust Score', String(cfg.trust_score ?? 0)],
        ['Verification', String(cfg.verification_tier ?? 'basic')],
        ['Auto-publish', cfg.auto_publish_capabilities ? chalk.green('yes') : chalk.dim('no')],
        ['Capabilities', Array.isArray(cfg.published_capabilities) && cfg.published_capabilities.length > 0
          ? cfg.published_capabilities.join(', ')
          : chalk.dim('none')],
        ['Status', rowStatus ?? '—'],
      ],
    );
    console.log();
  } catch (err) {
    spinner.fail('Failed to fetch config');
    error(err instanceof Error ? err.message : 'Unknown error');
    process.exitCode = 1;
  }
}

/**
 * `agt channels beam remove <agent-code-name>`
 */
export async function channelBeamRemoveCommand(agentCodeName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();

  if (!json) {
    const proceed = await confirm({
      message: `Remove Beam identity from "${agentCodeName}"? The directory entry will expire.`,
      default: false,
    });
    if (!proceed) return;
  }

  const spinner = ora({ text: 'Removing Beam identity…', isSilent: json });
  spinner.start();

  try {
    await api.del(`/agents/${encodeURIComponent(agentCodeName)}/channel-configs/beam`);
    spinner.succeed('Beam identity removed');

    if (json) {
      jsonOutput({ ok: true });
    }
  } catch (err) {
    spinner.fail('Failed to remove');
    error(err instanceof Error ? err.message : 'Unknown error');
    process.exitCode = 1;
  }
}
